import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type RevisionKind = 'barriers' | 'routes';

const STORAGE_PREFIX = 'rt.tileRevision';

interface TileRevisionValue {
  readonly barriersRevision: number;
  readonly routesRevision: number;
  readonly bumpBarriers: () => void;
  readonly bumpRoutes: () => void;
}

const TileRevisionContext = createContext<TileRevisionValue | undefined>(undefined);

interface TileRevisionProviderProps {
  readonly children: ReactNode;
}

/**
 * Monotonically-increasing tile revisions persisted to localStorage so a
 * page refresh does NOT reset them to `0`. That reset used to be a
 * correctness bug: MapLibre's tile URLs include `?v=<revision>`, browser
 * HTTP cache keys by URL, and the tile endpoint advertises
 * `Cache-Control: max-age=86400`. After a DELETE, the client would bump
 * to `v=1`; on refresh the fresh React state started again at `v=0`,
 * browser served the pre-delete `v=0` response out of its cache, and
 * the deleted barrier "reappeared" — independently of the server's
 * Redis tile cache being clean.
 *
 * Persisting the counter + using `Date.now()` as the first-ever seed
 * keeps URLs unique across sessions: every browser cache entry is tied
 * to a `v` value the app will never reuse, so a refresh after any
 * server mutation produces cache-miss + a fresh network fetch.
 */
export function TileRevisionProvider({ children }: TileRevisionProviderProps) {
  const [barriersRevision, setBarriersRevision] = useState(() => _readInitial('barriers'));
  const [routesRevision, setRoutesRevision] = useState(() => _readInitial('routes'));

  const bumpBarriers = useCallback(() => {
    setBarriersRevision((prev) => {
      const next = prev + 1;
      _write('barriers', next);
      return next;
    });
  }, []);

  const bumpRoutes = useCallback(() => {
    setRoutesRevision((prev) => {
      const next = prev + 1;
      _write('routes', next);
      return next;
    });
  }, []);

  const value = useMemo<TileRevisionValue>(
    () => ({ barriersRevision, routesRevision, bumpBarriers, bumpRoutes }),
    [barriersRevision, routesRevision, bumpBarriers, bumpRoutes],
  );

  return <TileRevisionContext.Provider value={value}>{children}</TileRevisionContext.Provider>;
}

export function useTileRevision(): TileRevisionValue {
  const ctx = useContext(TileRevisionContext);
  if (!ctx) {
    throw new Error('useTileRevision must be used within TileRevisionProvider');
  }
  return ctx;
}

function _storageKey(kind: RevisionKind): string {
  return `${STORAGE_PREFIX}.${kind}`;
}

function _readInitial(kind: RevisionKind): number {
  try {
    const raw = window.localStorage.getItem(_storageKey(kind));
    if (raw !== null) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) return n;
    }
  } catch {
    // Private-browsing / quota / SSR: fall through to the time seed.
  }
  // Seed with wall-clock ms so the very first session (and every private-
  // mode reload) gets a URL nobody has ever seen before.
  const seed = Date.now();
  _write(kind, seed);
  return seed;
}

function _write(kind: RevisionKind, value: number): void {
  try {
    window.localStorage.setItem(_storageKey(kind), String(value));
  } catch {
    // Silent.
  }
}
